import { useCallback, useMemo, useState } from 'react'
import { InventoryItem, Venue, InventoryType, VenueEvent } from 'types/model'

import {
  useVenue,
  useVenueDaySettings,
  useVenueLayouts,
  useVenueSections,
  useVenueTables,
} from '@modules/venue/data'
import { useVenueDayEvent } from '@modules/event/data'
import { findVenueEventForInventoryDate } from '@modules/event/findVenueEventForInventoryDate'
import { useInventoryList } from '@modules/inventory/data'
import { InventoryListView } from '@modules/inventory/views/inventory-list-view'
import { InventoryDetailModal } from '../views/inventory-detail-modal'

import {
  getBusinessDate,
  getCalendarDate,
  getInventoryBrowseDiaryDayKey,
  getOffsetUTC,
  isoToUTC,
  utcToISO,
} from '@utils/date-time'
import { formatPrice, getId } from '@utils/strings'
import { useCheckoutContext } from '@hooks/checkout'
import {
  VenueLayout,
  VenueLayoutSection,
  VenueLayoutTable,
} from 'types/model/venue-layout'
import { translateInventorySort } from '../utils'
import { useAttribution } from '@hooks/attribution'

/** API list shape so SWR/useDataList unwrap `data` consistently on SSR and the client (raw arrays break `metadataCasted.data` detection). */
function inventoryListResponseFallback(
  items: InventoryItem[] | undefined
): { data: InventoryItem[]; count: number } | undefined {
  if (!items) return undefined
  return { data: items, count: items.length }
}

/** Prefer SSR rows when SWR `data` is still `undefined` (common on SSR). Do not use `??` — `[]` must not mask SSR. */
function mergeSwrList<T>(swr: T[] | undefined, ssr: T[] | undefined): T[] {
  return swr === undefined ? (ssr ?? []) : swr
}

function sameInventoryBrowseDay(a: string, b: string): boolean {
  const ka = getInventoryBrowseDiaryDayKey(a)
  const kb = getInventoryBrowseDiaryDayKey(b)
  return ka != null && ka === kb
}

/**
 * Tickets/packages from getStaticProps match `initialDate`. SWR may be `undefined` on SSR
 * or `[]` before the client aligns — empty SWR + non-empty SSR on that same day must use SSR
 * so server/client first paint match. Off snapshot day, trust SWR (including real empty []).
 */
function mergeInventoryRowsForHydration(
  swr: InventoryItem[] | undefined,
  ssr: InventoryItem[] | undefined,
  browseDate: string | undefined,
  ssrSnapshotDate: string | undefined
): InventoryItem[] {
  const onSsrSnapshotDay =
    !!browseDate &&
    !!ssrSnapshotDate &&
    sameInventoryBrowseDay(browseDate, ssrSnapshotDate)
  if (!onSsrSnapshotDay) {
    return swr === undefined ? (ssr ?? []) : swr
  }
  if (swr === undefined || swr === null) return ssr ?? []
  if (swr.length === 0 && (ssr?.length ?? 0) > 0) return ssr ?? []
  return swr
}

export interface InventoryListProps {
  venue: Venue
  event?: VenueEvent
  initialDate?: string
  /** When set (e.g. from getStaticProps), keeps date picker bounds in sync with SSR and avoids hydration mismatch */
  inventoryCalendarMinDate?: number
  inventoryCalendarMaxDate?: number
  tickets?: InventoryItem[]
  ticketCount?: number
  packages?: InventoryItem[]
  packageCount?: number
  showHeader?: boolean
  stickyHeader?: boolean
  showDatePicker?: boolean
  showEvent?: boolean
  showEventPoster?: boolean
  stickyMap?: boolean
  hideMap?: boolean
  layouts?: VenueLayout[]
  sections?: VenueLayoutSection[]
  tables?: VenueLayoutTable[]
  /** When listing inventory without a fixed event (e.g. venue home), reuse SSR events to avoid duplicate /events calls for the selected day. */
  venueEventsForInventoryDay?: VenueEvent[]
}

export function InventoryList({
  venue: _venue,
  event: _event,
  tickets: _tickets,
  packages: _packages,
  layouts: _layouts = [],
  sections: _sections = [],
  tables: _tables = [],
  initialDate,
  inventoryCalendarMinDate,
  inventoryCalendarMaxDate,
  showEvent,
  showHeader = true,
  stickyHeader = true,
  showDatePicker = true,
  showEventPoster = false,
  stickyMap = false,
  hideMap = false,
  venueEventsForInventoryDay,
}: InventoryListProps) {
  const venueId = getId(_venue)
  const checkoutCtx = useCheckoutContext()
  const { affiliateId, attributionId } = useAttribution()

  const {
    venue,
    event,
    daySettings,
    tickets,
    packages,
    layouts,
    sections,
    tables,
    date,
    minDate,
    maxDate,
    handleDateChange,
  } = useInventoryListController({
    venueId,
    initialDate,
    inventoryCalendarMinDate,
    inventoryCalendarMaxDate,
    venueEventsForInventoryDay,
    fallbackData: {
      venue: _venue,
      event: _event,
      tickets: _tickets,
      packages: _packages,
      layouts: _layouts,
      sections: _sections,
      tables: _tables,
    },
  })

  const venueForView = (venue.data ?? _venue) as Venue

  const showMap =
    !hideMap &&
    venueForView &&
    (event.data?.isMapWidgetEnabled ??
      venueForView.eventDefault.isMapWidgetEnabled)

  return (
    <div className="w-full @container bg-inherit">
      <InventoryListView
        date={date}
        daySettings={daySettings}
        attributionId={attributionId}
        affiliateId={affiliateId}
        minDate={minDate}
        maxDate={maxDate}
        venue={venueForView}
        event={event.data}
        onDateChange={showDatePicker ? handleDateChange : undefined}
        packages={packages.data}
        packagesLoading={packages.loading && (packages.data?.length ?? 0) === 0}
        tickets={tickets.data}
        ticketsLoading={tickets.loading && (tickets.data?.length ?? 0) === 0}
        showHeader={showHeader}
        stickyHeader={stickyHeader}
        showEventHeader={showEvent}
        showEventPoster={showEventPoster}
        onInventoryAdd={checkoutCtx.selectInventory}
        stickyMap={stickyMap}
        showMap={showMap}
        layouts={layouts}
        sections={sections}
        tables={tables}
      />
      <InventoryDetailModal
        {...checkoutCtx.inventoryModal}
        venue={venueForView}
        event={event.data}
        inventory={checkoutCtx.selectedInventory}
        onCheckout={checkoutCtx.beginCheckout}
      />
    </div>
  )
}

export function useInventoryListController({
  venueId,
  initialDate: ssrInventorySnapshotDate,
  inventoryCalendarMinDate,
  inventoryCalendarMaxDate,
  venueEventsForInventoryDay,
  fallbackData,
}: {
  venueId: string
  initialDate?: string
  inventoryCalendarMinDate?: number
  inventoryCalendarMaxDate?: number
  venueEventsForInventoryDay?: VenueEvent[]
  fallbackData?: {
    venue: Venue
    event?: VenueEvent
    tickets?: InventoryItem[]
    packages?: InventoryItem[]
    layouts?: VenueLayout[]
    sections?: VenueLayoutSection[]
    tables?: VenueLayoutTable[]
  }
}) {
  const { venue, event, tickets, packages, layouts, sections, tables } =
    fallbackData ?? {}
  const venueTimezone = venue?.location?.timezone

  const [date, setDate] = useState<string | undefined>(() => {
    if (event) {
      return (
        utcToISO(event.date ?? ssrInventorySnapshotDate, {
          startOfDay: true,
        }) || undefined
      )
    }
    // When there is no event, prefer SSR/embed `initialDate` (e.g. venue home
    // `initialInventoryDate`) so the client matches getStaticProps and SWR keys
    // stay aligned—otherwise `getCalendarDate(now)` can drift from the server
    // near midnight and cause hydration mismatches.
    if (ssrInventorySnapshotDate != null && ssrInventorySnapshotDate !== '') {
      return ssrInventorySnapshotDate
    }
    // Consumer default is venue calendar day (matches upcoming events).
    // Nightlife business-day (pre-6am → yesterday) remains the min selectable date.
    return getCalendarDate(undefined, { timezone: venueTimezone }).toISOString()
  })
  const handleDateChange = useCallback((val?: number | string | null) => {
    const dt = val ? new Date(val).toISOString() : undefined
    setDate(dt)
  }, [])

  const dateUTC = useMemo(() => getOffsetUTC(date), [date])

  const inventoryParams = useMemo(
    () => ({
      // Inventory list endpoints treat dateStart/dateEnd as day filters, not
      // exact instants. Normalize to day-start so event start-time hours don't
      // leak into request params and shift day matching.
      dateStart: isoToUTC(date, { startOfDay: true })?.toString(),
      dateEnd: isoToUTC(date, { startOfDay: true })?.toString(),
      sort: translateInventorySort(venue?.inventorySortOrder),
      limit: 500,
      skip: 0,
    }),
    [date, venue?.inventorySortOrder]
  )

  const ticketParams = useMemo(
    () => ({
      ...inventoryParams,
      type: InventoryType.Ticket,
    }),
    [inventoryParams]
  )
  const packageParams = useMemo(
    () => ({
      ...inventoryParams,
      type: InventoryType.Package,
    }),
    [inventoryParams]
  )

  const currentVenue = useVenue(venueId, {
    config: { fallbackData: venue },
  })

  const venueDaySettings = useVenueDaySettings(venueId, dateUTC)

  const [minDate, maxDate] = useMemo(() => {
    if (inventoryCalendarMinDate != null && inventoryCalendarMaxDate != null) {
      return [inventoryCalendarMinDate, inventoryCalendarMaxDate]
    }
    const start = getBusinessDate(undefined, { timezone: venueTimezone })
    const calendarToday = getCalendarDate(undefined, {
      timezone: venueTimezone,
    })
    const purchaseWindow = currentVenue.data?.inventoryTimeframe ?? 30
    const end = new Date(
      calendarToday.getFullYear(),
      calendarToday.getMonth(),
      calendarToday.getDate() + purchaseWindow,
      0,
      0,
      0
    )
    return [start.valueOf(), end.valueOf()]
  }, [
    currentVenue.data?.inventoryTimeframe,
    inventoryCalendarMinDate,
    inventoryCalendarMaxDate,
    venueTimezone,
  ])

  const eventFromPreload = useMemo(() => {
    if (event) return undefined
    if (!venueEventsForInventoryDay?.length || !date) return undefined
    return findVenueEventForInventoryDate(venueEventsForInventoryDay, date)
  }, [date, event, venueEventsForInventoryDay])

  const dayEventQuery = useVenueDayEvent(venueId, date ?? '', {
    pause: !!event || !!eventFromPreload,
    config: { fallbackData: event ? [event] : undefined },
  })

  const resolvedDayEvent = event ?? eventFromPreload ?? dayEventQuery.data
  const currentTickets = useInventoryList(venueId, {
    params: ticketParams,
    config: {
      // useInventoryList types fallback as rows[]; SWR + useDataList expect API `{ data, count }`.
      fallbackData: inventoryListResponseFallback(tickets) as unknown as
        | InventoryItem[]
        | undefined,
    },
  })
  const currentPackages = useInventoryList(venueId, {
    params: packageParams,
    config: {
      fallbackData: inventoryListResponseFallback(packages) as unknown as
        | InventoryItem[]
        | undefined,
    },
  })

  const currentLayouts = useVenueLayouts(venue ? getId(venue) : null, {
    config: { fallbackData: layouts },
  })
  const currentSections = useVenueSections(venue ? getId(venue) : null, {
    config: { fallbackData: sections },
  })
  const currentTables = useVenueTables(venue ? getId(venue) : null, {
    config: { fallbackData: tables },
  })

  const venueForCurrency = currentVenue.data ?? venue

  const tierFlattenedTickets = useMemo(() => {
    let allTickets: InventoryItem[] = []
    const rawTickets = mergeInventoryRowsForHydration(
      currentTickets.data,
      tickets,
      date,
      ssrInventorySnapshotDate
    )
    if (!rawTickets.length) return allTickets
    for (let i = 0; i < rawTickets.length; ++i) {
      const inv = rawTickets[i]
      if (inv.hasTiers <= 1) {
        allTickets.push(inv)
      } else {
        let tierIndex = 0
        let activeTier = inv.tier.activeLevel
        const activeTierIndex = inv.tier.levels.findIndex(
          (tier) => tier.id === activeTier
        )

        while (tierIndex <= activeTierIndex) {
          const invDeepCopy = { ...inv }
          const currentTier = inv.tier.levels[tierIndex]
          invDeepCopy.price = currentTier.price
          invDeepCopy.name = `${invDeepCopy.name} - (${formatPrice(
            currentTier.price,
            {
              currency: venueForCurrency?.currencyCode,
              maximumFractionDigits: 0,
            }
          )})`
          if (tierIndex !== activeTierIndex) {
            invDeepCopy.quantity = 0
            invDeepCopy.quantityLeft = 0
            invDeepCopy.id = `${invDeepCopy.id}-${currentTier.id}`
          }
          allTickets.push(invDeepCopy)
          ++tierIndex
        }
      }
    }
    return allTickets
  }, [
    currentTickets.data,
    date,
    ssrInventorySnapshotDate,
    tickets,
    venueForCurrency?.currencyCode,
  ])

  const packageRows = mergeInventoryRowsForHydration(
    currentPackages.data,
    packages,
    date,
    ssrInventorySnapshotDate
  )

  return {
    venue: {
      ...currentVenue,
      data: currentVenue.data ?? venue,
    },
    event: { data: resolvedDayEvent },
    tickets: { ...currentTickets, data: tierFlattenedTickets },
    packages: { ...currentPackages, data: packageRows },
    layouts: mergeSwrList(currentLayouts.data, layouts),
    sections: mergeSwrList(currentSections.data, sections),
    tables: mergeSwrList(currentTables.data, tables),
    date,
    daySettings: venueDaySettings.data,
    minDate,
    maxDate,
    handleDateChange,
  }
}
